import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { DIMENSION_VALUE_NAMESPACE, toAgeKey } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';
import { RatingMatrixService } from '../../services';

export type ConditionPickerProperty = Property.Model<Property.PropertyType> | Conditions.Property;

export const MISSING_PROPERTY_MESSAGE =
  'This property no longer exists on the asset. Choose a replacement or remove the condition.';

/** Placeholder for a saved condition path that no longer resolves to a property on the set. */
export type MissingConditionProperty = Conditions.Property & { missing: true };

export function isMissingProperty(property: ConditionPickerProperty | null | undefined): boolean {
  return !!property && !!(property as MissingConditionProperty).missing;
}

function createMissingProperty(propertyPath: string, type: Conditions.PropertyType | null): MissingConditionProperty {
  const missingProperty: MissingConditionProperty = {
    id: propertyPath,
    type: type ?? Conditions.PropertyType.TEXT,
    name: propertyPath,
    valueKey: propertyPath,
    config: { format: null },
    isChild: false,
    missing: true,
  };
  return missingProperty;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-condition-property-picker',
  templateUrl: './condition-property-picker.component.html',
  styleUrls: ['./condition-property-picker.component.scss'],
  standalone: false,
})
export class ConditionPropertyPickerComponent {
  @Input() roundRightBorder = true;

  @PropertyListener('propertySet') propertySet$ = new BehaviorSubject<PropertySet.Model | null>(null);
  @Input() propertySet!: PropertySet.Model;

  /** Also offer scalar and categorical asset dimensions, not just properties. */
  @PropertyListener('includeDimensions') includeDimensions$ = new BehaviorSubject<boolean>(false);
  @Input() includeDimensions = false;

  overlayOpen = false;

  @PropertyListener('selectedPropertyPath') selectedPropertyPath$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() selectedPropertyPath?: string | null;

  /** Type saved on the condition — used to render the row when its property no longer exists. */
  @PropertyListener('missingPropertyType') missingPropertyType$ = new BehaviorSubject<Conditions.PropertyType | null>(
    null
  );
  @Input() missingPropertyType: Conditions.PropertyType | null = null;

  @Input() value: {
    property: Property.Model<Property.PropertyType> | Conditions.Property;
    propertyPath: string;
  } | null = null;

  @Output() selectionChanged = new EventEmitter<{
    property: {
      property: Property.Model<Property.PropertyType> | Conditions.Property;
      propertyPath: string;
    } | null;
    clearValue: boolean;
  }>();

  constructor(private ratingMatrixSvc: RatingMatrixService) {}

  // Bumped when the picker opens so the dimension list is refetched (see refreshDimensions).
  private refresh$ = new BehaviorSubject<void>(undefined);

  properties$ = combineLatest([this.propertySet$, this.includeDimensions$, this.refresh$]).pipe(
    switchMap(async ([set, includeDimensions]) => {
      const hardcodedProperties = Conditions.getProperties();

      const setProperties = withDerivedAgeProperties(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        set?.properties?.sort((a, b) => a.order - b.order).map(assignment => assignment.property!) ?? []
      );

      const properties = [...hardcodedProperties, ...setProperties];
      if (!includeDimensions || !set?.id) return properties;

      const dimensionEntries = await this.getDimensionEntries(set.id);
      return [...properties, ...dimensionEntries];
    })
  );

  selectedProperty$ = combineLatest([this.properties$, this.selectedPropertyPath$, this.missingPropertyType$]).pipe(
    map(([properties, selectedPropertyPath, missingPropertyType]) => {
      // A latched "missing" placeholder may re-resolve later (e.g. a transient dimension
      // fetch failure), so keep retrying until a real property is found.
      const missingSelected = isMissingProperty(this.value?.property);
      if (selectedPropertyPath && (selectedPropertyPath !== this.value?.propertyPath || missingSelected)) {
        const property = this.getProperty(selectedPropertyPath, properties);
        if (property) {
          this.setValue({ property, propertyPath: selectedPropertyPath }, false);
        } else if (!missingSelected || selectedPropertyPath !== this.value?.propertyPath) {
          // Fall back to a "missing" placeholder so the saved operator/value still render
          // when the property was removed from the set.
          this.setValue(
            {
              property: createMissingProperty(selectedPropertyPath, missingPropertyType),
              propertyPath: selectedPropertyPath,
            },
            false
          );
        }
      }
    })
  );

  readonly missingPropertyMessage = MISSING_PROPERTY_MESSAGE;

  get selectedPropertyMissing() {
    return isMissingProperty(this.value?.property);
  }

  private getProperty(
    propertyPath: string,
    properties: (Property.Model<Property.PropertyType> | Conditions.Property)[]
  ): Property.Model<Property.PropertyType> | Conditions.Property | undefined {
    for (const property of properties) {
      if (property.valueKey === propertyPath) {
        return property;
      } else if (propertyPath.startsWith(property.valueKey)) {
        return this.getProperty(
          propertyPath.replace(property.valueKey + '.', ''),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (property as Property.ObjectModel).properties?.map(assignment => assignment.property!) ?? []
        );
      }
    }
    return undefined;
  }

  setValue(
    value: { property: Property.Model<Property.PropertyType> | Conditions.Property; propertyPath: string } | null,
    clearValue: boolean
  ) {
    this.value = value;
    this.selectionChanged.emit({ property: value, clearValue });
  }

  open() {
    this.overlayOpen = true;
    this.refreshDimensions();
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
    if (this.overlayOpen) this.refreshDimensions();
  }

  // Dimensions can be created after the list was first cached — drop the cache and
  // refetch on open so newly-added ones show without a full page reload.
  private refreshDimensions() {
    if (!this.includeDimensions || !this.propertySet?.id) return;
    this.ratingMatrixSvc.invalidateDimensions(this.propertySet.id);
    this.refresh$.next(undefined);
  }

  private async getDimensionEntries(propertySetId: string): Promise<ConditionPickerProperty[]> {
    return getDimensionProperties(this.ratingMatrixSvc, propertySetId);
  }
}

// Scalar and categorical dimensions for a set, as condition-picker entries. Each emits a
// `dimension.<id>` path that resolves like a property at runtime. Shared with
// ConditionPreviewComponent so a saved dimension condition can be resolved to its name.
export async function getDimensionProperties(
  ratingMatrixSvc: RatingMatrixService,
  propertySetId: string
): Promise<ConditionPickerProperty[]> {
  try {
    const [scalarDimensions, categoricalDimensions] = await Promise.all([
      ratingMatrixSvc.listScalarDimensions(propertySetId),
      ratingMatrixSvc.listCategoricalDimensions(propertySetId),
    ]);

    const toProperty = (dimension: { id: string; title: string }, type: Conditions.PropertyType) =>
      ({
        id: dimension.id,
        type,
        name: dimension.title,
        valueKey: `${DIMENSION_VALUE_NAMESPACE}.${dimension.id}`,
        config: { format: null },
        isChild: false,
      }) as unknown as Conditions.Property;

    return [
      ...scalarDimensions.map(dimension => toProperty(dimension, Conditions.PropertyType.NUMBER)),
      ...categoricalDimensions.map(dimension => toProperty(dimension, Conditions.PropertyType.TEXT)),
    ];
  } catch {
    return [];
  }
}

export function withDerivedAgeProperties(properties: ConditionPickerProperty[]): ConditionPickerProperty[] {
  const nextProperties: ConditionPickerProperty[] = [];
  const existingValueKeys = new Set(properties.map(property => property.valueKey));

  for (const property of properties) {
    const nextProperty = withDerivedAgeChildren(property);
    nextProperties.push(nextProperty);

    if (!isDerivableYearProperty(property)) continue;

    const ageKey = toAgeKey(property.valueKey);
    if (existingValueKeys.has(ageKey)) continue;

    nextProperties.push(createDerivedAgeProperty(property, ageKey));
    existingValueKeys.add(ageKey);
  }

  return nextProperties;
}

function withDerivedAgeChildren(property: ConditionPickerProperty): ConditionPickerProperty {
  if (property.type !== Property.PropertyType.OBJECT) return property;

  const objectProperty = property as Property.ObjectModel;
  const assignments = objectProperty.properties ?? [];
  const existingValueKeys = new Set(assignments.map(assignment => assignment.property?.valueKey).filter(Boolean));
  const nextAssignments: Property.ObjectPropertyAssignment[] = [];

  for (const assignment of assignments) {
    const child = assignment.property;
    if (!child) {
      nextAssignments.push(assignment);
      continue;
    }

    const nextChild = withDerivedAgeChildren(child) as Property.Model<Property.PropertyType>;
    nextAssignments.push({ ...assignment, property: nextChild });

    if (!isDerivableYearProperty(child)) continue;

    const ageKey = toAgeKey(child.valueKey);
    if (existingValueKeys.has(ageKey)) continue;

    nextAssignments.push({
      objectId: assignment.objectId,
      order: assignment.order + 0.01,
      propertyId: `${child.id}__derived_age`,
      required: false,
      hidden: true,
      property: createDerivedAgeProperty(child, ageKey),
    });
    existingValueKeys.add(ageKey);
  }

  return {
    ...objectProperty,
    properties: nextAssignments,
  } as Property.ObjectModel;
}

function isDerivableYearProperty(property: ConditionPickerProperty): boolean {
  return property.type === Property.PropertyType.NUMBER && property.valueKey.toLowerCase().includes('year');
}

function createDerivedAgeProperty(source: ConditionPickerProperty, ageKey: string): Property.NumberModel {
  const timestamp = new Date(0);

  return {
    id: `${source.id}__derived_age`,
    type: Property.PropertyType.NUMBER,
    name: toAgeName(source.name),
    valueKey: ageKey,
    formId: null,
    config: { format: 'number' },
    created: 'created' in source ? source.created : timestamp,
    modified: 'modified' in source ? source.modified : timestamp,
  };
}

function toAgeName(name: string): string {
  const ageName = name.replace(/year/i, match => (match[0] === match[0].toUpperCase() ? 'Age' : 'age'));
  return ageName === name ? `${name} Age` : ageName;
}
